import React, { useState, useMemo, useCallback } from 'react';
import PropTypes from 'prop-types';
import UsersComponent from './UsersComponent';
import { useTranslation } from 'react-i18next';
import { useToast } from 'hooks/toasts/useToast';
import useConfirmDialog from 'features/common/components/confirm/useConfirmDialog';
import { useQuery, useMutation, useSubscription } from 'hooks/apollo';
import { addCachedDetail, removeCachedDetail } from 'utils/apollo/cacheExtensionFunctions';
import { USER_LIST_QUERY, USER_X_GROUP_QUERY } from '../../queries';
import { SAVE_USER_X_GROUP, DELETE_USER_X_GROUP } from '../../mutations';
import { USER_X_GROUP_SAVED, USER_X_GROUP_DELETED } from '../../subscriptions';

const UsersContainer = ({ userGroupId }) => {
  const addToast = useToast();
  const confirm = useConfirmDialog();
  const { t } = useTranslation();
  const [userXGroups, setUserXGroups] = useState([]);
  const [inAdd, setInAdd] = useState(false);

  const { data: userListData } = useQuery(USER_LIST_QUERY);

  const { loading } = useQuery(USER_X_GROUP_QUERY, {
    skip: !userGroupId,
    variables: { userGroupId },
    onCompleted: data => {
      const existingUserXGroups = (data?.userXGroup || []).map(x => ({
        userId: x,
        disabled: false,
        editing: false,
        isNew: false
      }));
      setUserXGroups(existingUserXGroups);
    }
  });

  const userXGroupsList = useMemo(() => {
    if (!userListData || !userListData.userList) {
      return [];
    }

    return userListData.userList.map(user => ({
      id: user.userId,
      name: `${user.userName} / ${user.firstName} ${user.lastName}`
    }));
  }, [userListData]);

  const updateLocalUserXGroup = useCallback((userId, property, value) => {
    setUserXGroups(prev => {
      const modifyUserXGroup = userXGroup => {
        const newUserXGroup = { ...userXGroup };
        newUserXGroup[property] = value;
        if (property === 'editing' && !value) newUserXGroup.disabled = false;
        return newUserXGroup;
      };
      const newUserXGroups = prev.map(x => (x.userId === userId ? modifyUserXGroup(x) : x));
      return newUserXGroups;
    });
  }, []);

  const [saveUserXGroup] = useMutation(SAVE_USER_X_GROUP, {
    onError: ({ params }) => {
      updateLocalUserXGroup(params.userId, 'disabled', false);
    }
  });

  const [deleteUserXGroup] = useMutation(DELETE_USER_X_GROUP, {
    onError: ({ params }) => {
      updateLocalUserXGroup(params.userId, 'disabled', false);
    }
  });

  const handleSave = useCallback(
    input => {
      if (userXGroups.some(x => x.userId === input.userId)) {
        addToast(t('UserGroups.Users.UserExists'), 'error');
        return;
      }
      updateLocalUserXGroup(input.initialUserId, 'disabled', true);
      updateLocalUserXGroup(input.initialUserId, 'initialUserId', input.initialUserId);

      const { userId, initialUserId } = input;
      saveUserXGroup({ variables: { input: { userGroupId: userGroupId, userId, initialUserId } } });
    },
    [addToast, saveUserXGroup, t, updateLocalUserXGroup, userGroupId, userXGroups]
  );

  const handleDelete = useCallback(
    userId => {
      confirm(
        () => {
          deleteUserXGroup({ variables: { userGroupId: userGroupId, userId } });
        },
        {
          content: t('UserGroups.Users.AreYouSureDeleteUser')
        }
      );
    },
    [confirm, t, deleteUserXGroup, userGroupId]
  );

  useSubscription(USER_X_GROUP_SAVED, {
    onData: ({
      data: {
        data: {
          userXGroupSaved: { userGroupId, userId }
        }
      },
      client
    }) => {
      var savedUserXGroup = userXGroups.find(x => x.initialUserId);
      setInAdd(false);
      addCachedDetail(
        client,
        { name: 'userXGroup', value: USER_X_GROUP_QUERY },
        { userGroupId },
        x => x === (savedUserXGroup?.initialUserId || userId),
        userId,
        []
      );
      addToast(t('UserGroups.Users.UserSaved'), 'success');
    }
  });

  useSubscription(USER_X_GROUP_DELETED, {
    onData: ({
      data: {
        data: {
          userXGroupDeleted: { userGroupId, userId }
        }
      },
      client
    }) => {
      setUserXGroups(prev => prev.filter(x => !(x.userId === userId)));
      removeCachedDetail(client, { name: 'userXGroup', value: USER_X_GROUP_QUERY }, { userGroupId }, x => x === userId, []);
      addToast(t('UserGroups.Users.UserDeleted'), 'success');
    }
  });

  const handleAdd = useCallback(() => {
    setUserXGroups(prev => [{ isNew: true, key: crypto.randomUUID(), userGroupId: userGroupId }, ...prev]);
    setInAdd(true);
  }, [userGroupId]);

  const handleCancel = useCallback(() => {
    setUserXGroups(prev => prev.filter((_, i) => i !== 0));
    setInAdd(false);
  }, []);

  const getUserXGroupKey = useCallback(
    userXGroup => (userXGroup.isNew ? userXGroup.key : `${userGroupId}_${userXGroup.userId}`),
    [userGroupId]
  );

  return (
    <UsersComponent
      existingUsers={userXGroups}
      loading={loading}
      userList={userXGroupsList}
      onSave={handleSave}
      onDelete={handleDelete}
      onCancel={handleCancel}
      onAdd={handleAdd}
      inAdd={inAdd}
      onUpdate={updateLocalUserXGroup}
      getUserXGroupKey={getUserXGroupKey}
    />
  );
};

UsersContainer.propTypes = {
  userGroupId: PropTypes.number.isRequired
};

export default UsersContainer;
